/**
 * PLATINUM — Multi-tier cart progress bar (v3 — ladder display, 2026-08)
 *
 * Upgrades the Prestige <free-shipping-bar> (drawer + cart page) into a
 * milestone ladder: free shipping -> gift tier(s). Pure display: the gift
 * itself is handled by the cart-upsell gift ladder (customer PICKS one reward,
 * discount code zeroes it). This bar only shows progress and points to the
 * picker.
 *
 * Config sources:
 *   - window.PlatinumTierBarConfig (texts) — snippets/platinum-tier-bar.liquid
 *   - window.PlatinumUpsellConfig.giftLadder.tiers (threshold, name, enabled,
 *     onlyPet) — snippets/platinum-cart-upsell.liquid, single source of truth
 *     shared with the gift picker so the two can never disagree.
 *
 * Threshold basis: paid price of NON-gift lines (final_line_price) — same as
 * the gift ladder and Prestige's own bar. Graceful fallback: no JS -> native
 * Prestige text; no enabled tiers -> plain single-milestone shipping bar.
 */
(function () {
  'use strict';

  var CFG = window.PlatinumTierBarConfig || {};
  var map = null;
  var busy = false;
  var refreshQueued = false;

  function money(n) {
    return n.toLocaleString('sl-SI', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  }

  /** A line the gift ladder added — €0, must not count toward thresholds. */
  function isGiftLine(item) {
    return !!(item.properties && item.properties._platinum_upsell === 'cart-gift');
  }

  async function loadMap() {
    if (map) return map;
    try {
      var up = window.PlatinumUpsellConfig;
      var url = CFG.mapUrl || (up && up.mapUrl);
      map = url ? await (await fetch(url, { credentials: 'same-origin' })).json() : {};
    } catch (e) {
      map = {};
    }
    return map;
  }

  /** Dominant species by paid value — only needed for tiers with onlyPet. */
  function classify(cart) {
    var dog = 0;
    var cat = 0;
    (cart.items || []).forEach(function (item) {
      if (isGiftLine(item)) return;
      var entry = map[item.handle];
      var pet = entry && !Array.isArray(entry) ? entry.pet : null;
      if (!pet) {
        var t = ((item.product_type || '') + ' ' + (item.product_title || item.title || '')).toLowerCase();
        if (/ma[cč]k|meatcrisp/.test(t)) pet = 'cat';
      }
      if (pet === 'cat') cat += item.final_line_price;
      else if (pet === 'dog') dog += item.final_line_price;
    });
    if (!dog && !cat) return null;
    return cat > dog ? 'cat' : 'dog';
  }

  /** Enabled ladder tiers applicable to this cart, ascending by threshold. */
  function activeTiers(species) {
    var up = window.PlatinumUpsellConfig;
    var L = up && up.giftLadder;
    if (!L || !L.enabled || !Array.isArray(L.tiers)) return [];
    if (L.endsAt && Date.now() > Date.parse(L.endsAt)) return [];
    return L.tiers
      .filter(function (t) {
        return t.enabled !== false && t.threshold > 0 && Array.isArray(t.variants) && t.variants.length &&
          (!t.onlyPet || t.onlyPet === species);
      })
      .slice()
      .sort(function (a, b) { return a.threshold - b.threshold; });
  }

  /**
   * Tiered promo (AKCIJA Stars, 2026-09 — PlatinumUpsellConfig.gift.tiers): its milestones join the
   * bar like the kapa/majica ladder did, but are measured on the configured product types only
   * (dog food, paid price after the Appstle package) — same basis as the cart-upsell picker.
   * Returns null outside the promo window or for a cat-only cart.
   */
  function promoState(cart, species) {
    var up = window.PlatinumUpsellConfig;
    var G = up && up.gift;
    if (!G || !G.enabled || !Array.isArray(G.tiers) || !G.tiers.length) return null;
    var now = Date.now();
    if (G.startsAt && now < Date.parse(G.startsAt)) return null;
    if (G.endsAt && now > Date.parse(G.endsAt)) return null;
    var ids = {};
    (G.variants || []).forEach(function (v) { ids[Number(v.id)] = true; });
    var types = (G.basisProductTypes || []).map(function (s) { return String(s).toLowerCase(); });
    var basis = 0;
    var units = 0;
    (cart.items || []).forEach(function (item) {
      if (ids[Number(item.variant_id)]) { units += item.quantity; return; } // gift product: never counts toward its own threshold
      if (isGiftLine(item)) return;
      if (!types.length || types.indexOf(String(item.product_type || '').toLowerCase()) !== -1) basis += item.final_line_price;
    });
    if (!basis && species === 'cat') return null;
    return {
      G: G,
      basis: basis / 100,
      units: units,
      tiers: G.tiers.slice().sort(function (a, b) { return Number(a.threshold) - Number(b.threshold); }),
    };
  }

  async function evaluate() {
    if (busy) { refreshQueued = true; return; }
    busy = true;
    try {
      await loadMap();
      var cart = await (await fetch('/cart.js', { credentials: 'same-origin' })).json();

      var base = 0;
      var giftInCart = false;
      var giftVariantIds = {};
      (cart.items || []).forEach(function (item) {
        if (isGiftLine(item)) { giftInCart = true; giftVariantIds[item.variant_id] = true; }
        else base += item.final_line_price;
      });
      var total = base / 100;
      var species = classify(cart);
      var tiers = activeTiers(species);
      // per-tier: does the cart hold this tier's gift? is it auto-added (no choice)?
      tiers.forEach(function (t) {
        t.__has = t.variants.some(function (v) { return giftVariantIds[v.id]; });
        t.__auto = t.autoAdd === true || (t.autoAdd !== false && t.variants.length === 1);
      });
      var promo = promoState(cart, species);

      document.querySelectorAll('free-shipping-bar').forEach(function (bar) {
        renderBar(bar, total, tiers, giftInCart, promo);
      });
      maskGiftCodes();
      decorateGiftLines(cart);
      renderCartSummary(cart);
    } catch (e) {
      console.warn('[Platinum TierBar] evaluate failed:', e);
    } finally {
      busy = false;
      if (refreshQueued) { refreshQueued = false; evaluate(); }
    }
  }

  function renderBar(bar, total, tiers, giftInCart, promo) {
    // Prestige stores money attrs in CENTS (threshold="2900") — our total is in EUR
    var thrAttr = parseFloat(bar.getAttribute('threshold'));
    var shippingThreshold = thrAttr > 0 ? thrAttr / 100 : (CFG.shippingThreshold || 29);
    // milestones: shipping + every tier (deduped by threshold), ascending
    var milestones = [{ key: 'ship', threshold: shippingThreshold, label: CFG.shippingLabel || 'dostava', icon: '🚚' }];
    tiers.forEach(function (t, i) {
      milestones.push({ key: 'tier' + i, threshold: t.threshold, label: t.label || t.name || (CFG.giftLabel || 'darilo'), icon: t.icon || '🎁', tier: t });
    });
    if (promo) {
      promo.tiers.forEach(function (t, i) {
        milestones.push({ key: 'promo' + i, threshold: Number(t.threshold), label: t.label || (i + 1) + '×', icon: t.icon || '🎁', promo: true });
      });
    }
    milestones.sort(function (a, b) { return a.threshold - b.threshold; });
    // same threshold (e.g. shipping 29 € + a 29 € tier) -> ONE dot, labels joined
    milestones = milestones.reduce(function (acc, m) {
      var prev = acc[acc.length - 1];
      if (prev && Math.abs(prev.threshold - m.threshold) < 0.005) {
        prev.label = prev.label + ' + ' + m.label;
        prev.icon = '🎁';
        prev.key = prev.key + '+' + m.key;
      } else {
        acc.push(m);
      }
      return acc;
    }, []);
    var maxThreshold = milestones[milestones.length - 1].threshold;
    // promo carts: the gift part of the bar runs on the promo basis (dog food), the shipping
    // part on the whole cart — max() keeps the fill truthful for both (a 40 € cart without dog
    // food fills to the truck, not past a Stars dot)
    var value = promo ? Math.max(promo.basis, Math.min(total, shippingThreshold)) : total;
    // 3+ milestones sit evenly (29 € and 35 € would collide proportionally); the fill interpolates between them
    var even = milestones.length >= 3;
    var pos = function (v) {
      if (!even) return Math.max(0, Math.min(100, (v / maxThreshold) * 100));
      var prevT = 0;
      var prevP = 0;
      for (var j = 0; j < milestones.length; j++) {
        var t = milestones[j].threshold;
        var p = ((j + 1) / milestones.length) * 100;
        if (v <= t) return prevP + (t > prevT ? (Math.max(v, prevT) - prevT) / (t - prevT) : 1) * (p - prevP);
        prevT = t;
        prevP = p;
      }
      return 100;
    };
    var reachedAt = function (m) { return (m.promo ? promo.basis : total) + 0.005 >= m.threshold; };
    var pct = pos(value);
    var shippingReached = total >= shippingThreshold;
    var nextTier = null;
    for (var i = 0; i < tiers.length; i++) { if (total < tiers[i].threshold) { nextTier = tiers[i]; break; } }
    var allReached = milestones.every(reachedAt);

    var root = bar.querySelector('.ptb');
    if (!root) {
      root = document.createElement('div');
      root.className = 'ptb';
      root.innerHTML = '<p class="ptb-msg" role="status"></p><div class="ptb-track"><div class="ptb-fill"></div></div>';
      bar.appendChild(root);
      bar.classList.add('ptb-host');
    }
    var track = root.querySelector('.ptb-track');
    root.classList.toggle('ptb--all-reached', allReached);
    root.querySelector('.ptb-fill').style.width = pct.toFixed(1) + '%';

    // (re)build dots only when the milestone set changes
    var sig = milestones.map(function (m) { return m.key + ':' + m.threshold; }).join('|');
    if (track.getAttribute('data-sig') !== sig) {
      track.querySelectorAll('.ptb-dot').forEach(function (d) { d.remove(); });
      milestones.forEach(function (m) {
        var dot = document.createElement('div');
        dot.className = 'ptb-dot';
        dot.setAttribute('data-key', m.key);
        dot.textContent = m.icon;
        var label = document.createElement('span');
        label.className = 'ptb-dot-label';
        label.textContent = m.label;
        dot.appendChild(label);
        dot.style.setProperty('--ptb-pos', pos(m.threshold).toFixed(1) + '%');
        track.appendChild(dot);
      });
      track.setAttribute('data-sig', sig);
    }
    milestones.forEach(function (m) {
      var dot = track.querySelector('.ptb-dot[data-key="' + m.key + '"]');
      if (dot) dot.classList.toggle('ptb-dot--reached', reachedAt(m));
    });

    var msg = root.querySelector('.ptb-msg');
    var amount = function (x) { return '<span class="ptb-amount">' + money(x) + '</span>'; };
    if (promo) {
      // tiered promo texts (config: PlatinumUpsellConfig.gift.bar) — shipping first, then the tiers
      var PT = promo.G.bar || {};
      var level = 0;
      promo.tiers.forEach(function (t, k) { if (promo.basis + 0.005 >= Number(t.threshold)) level = k + 1; });
      var slots = level ? Number(promo.tiers[level - 1].slots || level) : 0;
      var nextP = promo.tiers[level] || null;
      if (!shippingReached) {
        msg.innerHTML = (CFG.shippingText || 'Za <strong>brezplačno dostavo</strong> ti manjka še {x}.').replace('{x}', amount(shippingThreshold - total));
      } else if (level && promo.units < slots) {
        msg.innerHTML = PT.pickText || 'Dostava je brezplačna 🎉 <strong>Izberi svoje darilo</strong> spodaj 🎁';
      } else if (nextP) {
        var ordinal = ['prve', 'druge', 'tretje', 'četrte', 'pete'][level] || String(level + 1) + '.';
        msg.innerHTML = ((level ? PT.nextText : PT.teaserText) || 'Za naslednje darilo ti manjka še {x}.')
          .replace('{n}', String(level + 1))
          .replace('{nw}', ordinal)
          .replace('{x}', amount(Number(nextP.threshold) - promo.basis));
      } else {
        msg.innerHTML = PT.doneText || CFG.giftDoneText || 'Čestitamo! 🎉';
      }
      return;
    }
    var pendingPick = null;
    for (var k = 0; k < tiers.length; k++) {
      if (total >= tiers[k].threshold && !tiers[k].__has && !tiers[k].__auto) { pendingPick = tiers[k]; break; }
    }
    if (!shippingReached) {
      msg.innerHTML = (CFG.shippingText || 'Za <strong>brezplačno dostavo</strong> ti manjka še {x}.')
        .replace('{x}', '<span class="ptb-amount">' + money(shippingThreshold - total) + '</span>');
    } else if (pendingPick) {
      msg.innerHTML = (CFG.giftPickText || 'Dostava je brezplačna 🎉 <strong>Izberi svoje darilo</strong> — {name} — spodaj 🎁')
        .replace('{name}', pendingPick.name || 'darilo');
    } else if (nextTier) {
      msg.innerHTML = (giftInCart
        ? (CFG.giftNextText || 'Darilo je v košarici 🎁 Za naslednje — <strong>{name}</strong> — ti manjka še {x}.')
        : (CFG.giftTeaserText || 'Dostava je brezplačna 🎉 Za darilo — <strong>{name}</strong> — ti manjka še {x}.'))
        .replace('{name}', nextTier.name || 'darilo')
        .replace('{x}', '<span class="ptb-amount">' + money(nextTier.threshold - total) + '</span>');
    } else if (tiers.length && giftInCart) {
      msg.innerHTML = CFG.giftDoneText || 'Brezplačna dostava in darila 🎁 — vse tvoje!';
    } else {
      msg.innerHTML = CFG.shippingDoneText || 'Dostava je brezplačna 🎉';
    }
  }

  /**
   * Gift lines in the theme's cart UI: prefix the title with 🎁 and move them
   * to the top of the list (Prestige: <line-item> inside .cart-drawer__items /
   * cart page list; title = a[href*="variant=ID"]). Cosmetic, idempotent.
   */
  function decorateGiftLines(cart) {
    var gifts = (cart.items || []).filter(isGiftLine);
    if (!gifts.length) return;
    // match by LINE KEY: a tin of the same flavour the customer bought himself is never dressed as a gift
    var byKey = {};
    gifts.forEach(function (g) { byKey[g.key] = g; });
    var up = window.PlatinumUpsellConfig;
    var G = up && up.gift && Array.isArray(up.gift.tiers) ? up.gift : null;
    var promoVariant = {};
    if (G) (G.variants || []).forEach(function (v) { promoVariant[Number(v.id)] = v; });
    document.querySelectorAll('line-item').forEach(function (li) {
      var keyEl = li.querySelector('[data-line-key]');
      var key = keyEl ? keyEl.getAttribute('data-line-key') : null;
      if (!key) {
        var ch = li.querySelector('a[href*="/cart/change?id="]');
        var km = ch && ch.getAttribute('href').match(/id=([^&]+)/);
        key = km ? decodeURIComponent(km[1]) : null;
      }
      var g = key && byKey[key];
      if (!g) return;
      var a = li.querySelector('a[href*="variant="]');
      var pv = promoVariant[Number(g.variant_id)];
      if (a) {
        // tiered promo (Stars, Igor's review 21. 9.): short name "Iberico Stars 185 g – darilo"
        var title = pv ? '🎁 ' + (pv.name || a.textContent.trim()) + ' – ' + (G.giftWord || 'darilo') : null;
        if (title) { if (a.textContent !== title) a.textContent = title; }
        else if (a.textContent.indexOf('🎁') !== 0) a.textContent = '🎁 ' + a.textContent.trim();
      }
      if (pv) {
        li.classList.add('ptb-gift-line--promo');
        if (G.lineDesc && a && !li.querySelector('.ptb-gift-desc')) {
          var desc = document.createElement('p');
          desc.className = 'ptb-gift-desc';
          desc.textContent = G.lineDesc;
          a.insertAdjacentElement('afterend', desc);
        }
        // "DARILO" instead of €0,00 — only when the line really is free
        var sale = li.querySelector('sale-price');
        if (sale && g.final_line_price === 0) {
          sale.childNodes.forEach(function (n) {
            if (n.nodeType === 3 && /0[,.]00/.test(n.nodeValue)) n.nodeValue = ' ' + (G.freeWord || 'DARILO');
          });
        }
        // cart PAGE: quantity + "Odstrani" also live in their own table cell (desktop) -> mark the whole row
        var row = li.closest('tr');
        if (row) row.classList.add('ptb-gift-row--promo');
        // "Zamenjaj okus" next to "Odstrani": take this gift out, the flavour tiles come back
        (row || li).querySelectorAll('line-item-quantity').forEach(function (qty) {
          if (qty.querySelector('.ptb-gift-swap')) return;
          var swap = document.createElement('button');
          swap.type = 'button';
          swap.className = 'ptb-gift-swap link text-xs';
          swap.textContent = G.swapText || 'Zamenjaj okus';
          swap.addEventListener('click', function () { swapGift(key, swap); });
          qty.insertBefore(swap, qty.firstChild);
        });
      }
      if (!li.classList.contains('ptb-gift-line')) {
        li.classList.add('ptb-gift-line');
        var parent = li.parentElement;
        if (parent && parent.firstElementChild !== li) parent.insertBefore(li, parent.firstElementChild);
      }
    });
  }

  /** Remove one gift line; the cart-upsell picker then offers the flavours again (and scrolls to them). */
  function swapGift(key, btn) {
    btn.disabled = true;
    try { sessionStorage.setItem('pgift-focus', '1'); } catch (e) {}
    var updates = {};
    updates[key] = 0;
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      body: JSON.stringify({ updates: updates }),
    }).catch(function () {}).then(function () {
      if (document.querySelector('.cart-page')) window.location.reload();
      else document.dispatchEvent(new CustomEvent('cart:refresh'));
    });
  }

  /**
   * The theme prints the discount title (= the code, e.g. "DARILO-BLD48") on the
   * gift line badge. Customers don't need to see/share the code — relabel it
   * "Darilo" wherever it appears in cart UI. Cosmetic only; the code still works.
   */
  function maskGiftCodes() {
    var up = window.PlatinumUpsellConfig;
    var L = up && up.giftLadder;
    var def = CFG.codeLabel || 'Darilo';
    var pairs = [];
    if (L) [L.discountCode].concat((L.tiers || []).map(function (t) { return t.discountCode; })).forEach(function (c) { if (c) pairs.push([c, def]); });
    if (up && up.gift && up.gift.discountCode) pairs.push([up.gift.discountCode, def]); // MENU akcija
    // tiered promo (Stars): STARS1/2/3 read as "Darilo Stars" in the cart; the code stays on the order
    if (up && up.gift && Array.isArray(up.gift.tiers)) {
      up.gift.tiers.forEach(function (t) { if (t.discountCode) pairs.push([t.discountCode, up.gift.codeLabel || def]); });
    }
    if (!pairs.length) return;
    var rules = pairs.map(function (p) {
      return [new RegExp(p[0].replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), p[1]];
    });
    var roots = document.querySelectorAll('cart-drawer, .cart-drawer, .cart-page, #cart-drawer, [id*="cart-drawer"], .cart');
    roots.forEach(function (root) {
      // walk TEXT nodes (the badge mixes an svg icon + text in one element)
      var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      var node;
      while ((node = walker.nextNode())) {
        var txt = node.nodeValue;
        if (!txt) continue;
        var changed = txt;
        rules.forEach(function (r) { changed = changed.replace(r[0], r[1]); });
        if (changed !== txt) node.nodeValue = changed;
      }
    });
  }


  /**
   * Price transparency right above the checkout button (audit 2026-08-21):
   * vmesni seštevek · popusti · dostava (29 € free / 4 € / 2 € samples) · skupaj,
   * plus an optional trust line. Shipping tiers mirror the SI delivery profile.
   */
  function renderCartSummary(cart) {
    var buttons = document.querySelectorAll('#cart-drawer [name="checkout"], .cart-drawer [name="checkout"], cart-drawer [name="checkout"], .cart-page [name="checkout"]');
    if (!buttons.length) return;
    var paid = (cart.total_price || 0) / 100;               // after all discounts
    var before = (cart.original_total_price || cart.items_subtotal_price || 0) / 100;
    var discount = Math.max(0, ((cart.original_total_price || 0) - (cart.total_price || 0)) / 100);
    var freeFrom = CFG.shippingThreshold || 29;
    var ship = paid >= freeFrom ? 0 : (paid <= 0.5 ? (CFG.shippingSamples != null ? CFG.shippingSamples : 2) : (CFG.shippingStandard != null ? CFG.shippingStandard : 4));
    var L = CFG.summary || {};
    // compact (2026-08-23, Igor: footer too tall) — one muted info line + bold total; subtotal is on checkout anyway
    var info = [];
    if (discount > 0.004) info.push((L.discount || 'Popusti in darila') + ' − ' + money(discount));
    info.push((L.shipping || 'Dostava') + ' ' + (ship === 0 ? (L.free || 'brezplačna').toLowerCase() : money(ship)));
    var htmlFull = '<div class="ptb-sum">' +
      '<div class="ptb-sum__info">' + info.join(' · ') + '</div>' +
      '<div class="ptb-sum__row ptb-sum__row--total"><span>' + (L.total || 'Skupaj') + '</span><span>' + money(paid + ship) + '</span></div>' +
      '</div>';
    // cart PAGE: Prestige already prints "Skupaj: …" in .cart-recap → only the muted info line (no duplicate total)
    var htmlInfo = '<div class="ptb-sum ptb-sum--info-only"><div class="ptb-sum__info">' + info.join(' · ') + '</div></div>';
    buttons.forEach(function (btn) {
      var host = btn.parentElement;
      if (!host) return;
      var html = btn.closest('.cart-recap') ? htmlInfo : htmlFull;
      var el = host.querySelector(':scope > .ptb-sum');
      if (!el) {
        el = document.createElement('div');
        host.insertBefore(el, btn);
      }
      if (el.getAttribute('data-html') !== html) {
        el.outerHTML = html.replace('<div class="ptb-sum">', '<div class="ptb-sum" data-html="">');
        el = host.querySelector(':scope > .ptb-sum');
        el.setAttribute('data-html', html);
      }
      // trust line BELOW the button (less weight above the CTA)
      if (CFG.trustText) {
        var tr = host.querySelector(':scope > .ptb-sum__trust');
        if (!tr) { tr = document.createElement('div'); tr.className = 'ptb-sum__trust'; host.appendChild(tr); }
        if (tr.innerHTML !== CFG.trustText) tr.innerHTML = CFG.trustText;
      }
    });
    compactFooter();
  }

  /** Collapse the discount-code field behind a small link next to "Dodaj opombo" (footer height). */
  function compactFooter() {
    var footers = document.querySelectorAll('#cart-drawer .cart-drawer__footer, cart-drawer .cart-drawer__footer');
    footers.forEach(function (f) {
      var disc = f.querySelector(':scope > .cart-discount');
      if (!disc || disc.hasAttribute('data-ptb-compact')) return;
      disc.setAttribute('data-ptb-compact', '');
      var applied = !!f.querySelector('.discount-badge');
      var row = f.querySelector(':scope > .v-stack');            // holds the "Dodaj opombo" link
      if (!row) { row = document.createElement('div'); row.className = 'v-stack gap-0.5 justify-items-start'; f.insertBefore(row, disc); }
      row.classList.add('ptb-footer-links');
      var link = document.createElement('button');
      link.type = 'button'; link.className = 'ptb-discount-toggle';
      link.innerHTML = '<span class="ptb-discount-toggle__icon" aria-hidden="true">&#127991;&#65038;</span><span>' + ((CFG.summary && CFG.summary.discountLink) || 'Koda za popust') + '</span><span class="ptb-discount-toggle__chev" aria-hidden="true"></span>';
      link.setAttribute('aria-expanded', 'false');
      link.addEventListener('click', function () {
        var open = disc.hasAttribute('hidden');
        if (open) { disc.removeAttribute('hidden'); var inp = disc.querySelector('input:not([type=hidden])'); if (inp) inp.focus(); }
        else disc.setAttribute('hidden', '');
        link.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      // discount first (primary, left); Prestige "Dodaj opombo" becomes secondary (right, muted)
      row.insertBefore(link, row.firstChild);
      var noteBtn = row.querySelector('button[aria-controls^="cart-note-"]');
      if (noteBtn) noteBtn.classList.add('ptb-note-toggle');
      disc.setAttribute('hidden', '');                         // always collapsed initially (applied codes show as badge)
      addApplyButton(disc);
    });
    // cart PAGE: field stays visible (there is room), but gets the same "Uporabi" button
    document.querySelectorAll('.cart-recap .cart-discount').forEach(addApplyButton);
  }

  /** Explicit "Uporabi" button — Prestige applies a code only on change (Enter / blur), unclear on mobile. */
  function addApplyButton(disc) {
    var field = disc.querySelector('cart-discount-field');
    var input = field && field.querySelector('input:not([type=hidden])');
    if (!field || !input || field.querySelector('.ptb-discount-apply')) return;
    var apply = document.createElement('button');
    apply.type = 'button'; apply.className = 'ptb-discount-apply';
    apply.textContent = (CFG.summary && CFG.summary.applyLabel) || 'Uporabi';
    apply.addEventListener('click', function () {
      if (!input.value.trim()) { input.focus(); return; }
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    input.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); input.dispatchEvent(new Event('change', { bubbles: true })); } });
    field.appendChild(apply);
    field.classList.add('ptb-has-apply');
  }

  /* ---------------- wiring ---------------- */

  var debounceTimer = null;
  function scheduleEvaluate() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(evaluate, 150);
  }

  function watch(bar) {
    if (bar.__ptbWatched) return;
    bar.__ptbWatched = true;
    new MutationObserver(scheduleEvaluate).observe(bar, {
      attributes: true,
      attributeFilter: ['total-price', 'threshold'],
    });
  }

  function init() {
    document.querySelectorAll('free-shipping-bar').forEach(watch);
    new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.tagName === 'FREE-SHIPPING-BAR') { watch(node); scheduleEvaluate(); }
          else if (node.querySelector && node.querySelector('free-shipping-bar')) {
            node.querySelectorAll('free-shipping-bar').forEach(watch);
            scheduleEvaluate();
          }
        });
      });
    }).observe(document.body, { childList: true, subtree: true });

    document.addEventListener('cart:change', scheduleEvaluate);
    document.addEventListener('cart-drawer:refreshed', scheduleEvaluate);
    scheduleEvaluate();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
